<template>
  <Accordion :activeIndex="0" class="mt-4" @tab-close="handleTabClose">
    <AccordionTab :header="accordionHeader">
      <div class="mb-1">
        <span class="label-span">Tumor Board Name</span>
      </div>
      <AccordionNameInput :error-message="errorMessage" :tumor-board-name="tumorBoardObj.tb_name"
        :original-name="values.tb_name" @update-name-error-message="updateErrorMessage"
        @update-tumor-board-name="updateTumorBoardName" />
      <AccordionDateInput :time-zone="userTimeZone" :time-and-date-obj="tumorBoardObj" :frequency="nonModalFrequency"
        @date-time-update="handleDateTimeUpdate" />
      <div class="field-margin">
        <span class="label-span">Occurrence</span>
      </div>
      <Dropdown v-model="nonModalFrequency" :options="recurrenceOptions" optionLabel="name"
        placeholder="Select Occurrence" class="custom-placeholder-color"
        @change="handleModalOpenClose(`recurrenceDropdown`)" />
      <div v-if="recurrenceMessage" class="mt-4">
        <span class="recurrence-message">{{ recurrenceMessage }}</span>
      </div>
      <div class="field-margin">
        <span class="label-span">Location</span>
      </div>
      <AccordionLocationInput :locationErrorMessage="locationErrorMessage"
        :tumorBoardLocation="tumorBoardObj.tb_occurrence.location" @update-tumor-board-location="updateTumorBoardLocation"
        @update-location-error-message="updateLocationErrorMessage" />
      <div class="save-div">
        <button @click="saveTumorBoard()" class="primary-filled-button" :disabled="!isFormEnabled">
          Save</button>
      </div>
    </AccordionTab>
  </Accordion>
  <RecurrenceModal :isRecurrenceModalOpen="isRecurrenceModalOpen" :nonModalFrequency="nonModalFrequency"
    :modalValues="tumorBoardObj" :isResetModal="isResetRecurrenceModal" @set-state-from-modal="setStateFromModal"
    @handle-modal-close="handleModalClose" @handle-modal-cancel="handleModalCancel" />

  <Dialog v-model:visible="forceUpdate" modal header="Info">
    <p>This tumor board has patients assigned to it. Please reassign patients to a different tumor board from the
      “To Be Scheduled” and “Scheduled” tables before updating this tumor board.</p>
    <div class="flex gap-2">
      <button class="secondary-outlined-button cancel-btn" @click.prevent="handleCloseForceUpdate">Cancel</button>
      <!-- TODO: Confirm button may require in future, anytime requirement may change, keeping it in commented section. -->
      <!-- <button class="primary-filled-button save-btn" @click.prevent="handleConfirmForceUpdate">Confirm</button> -->
    </div>
  </Dialog>
</template>

<script setup>
// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, defineEmits, defineProps, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import moment from 'moment-timezone'
import _ from 'lodash'
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'
import Dropdown from 'primevue/dropdown'
import Dialog from 'primevue/dialog'
import RecurrenceModal from './RecurrenceModal.vue'
import AccordionNameInput from './AccordionNameInput.vue'
import AccordionLocationInput from './AccordionLocationInput.vue'
import AccordionDateInput from './AccordionDateInput.vue'
import { recurrenceOptions } from '@/common/shared.js'
import { formatDate, formatDateToTBPayload, showToast } from '@/utils/utils.js'
import { putTumorBoard } from '@/ux3/apis/putTumorBoard.js'
import { sendCalendarInvite } from '@/ux3/apis/sendCalendarInvite.js'
import { updateCalendarInvite } from '@/ux3/apis/updateCalendarInvite.js'

const props = defineProps({
  indexOfTB: {
    type: Number,
    default: -1,
  },
  values: {
    type: Object,
    default: () => ({}),
  },
})

const store = useStore()
const emit = defineEmits(['accordionTabClose', 'form-change', 'reloadComponent', 'reset-confirmation'])

const editModeActive = ref(false)
const timeZone = ref('')
const nonModalFrequency = ref({ id: 'DO NOT REPEAT', name: 'Do not Repeat' })
const recurrenceMessage = ref('')
const isRecurrenceModalOpen = ref(false)
const isResetRecurrenceModal = ref(false)
const errorMessage = ref('')
const locationErrorMessage = ref('')
const forceUpdate = ref(false)
const accordionHeader = ref('Add Tumor Board')
const indexOfCurrentTb = ref(-1)

const accordionFields = computed(() => props.values)
const userTimeZone = computed(() => store.getters.profile.timeZone)

const freqFromProps = accordionFields.value?.tb_occurrence?.frequency

// set default values of the tumor board object
const defaultStartDate = moment().format('DD-MMM-YYYY')
const defaultStartTime = moment().format('HH:mm')
const defaultEndTime = moment().add(60, 'minutes').format('HH:mm')
const defaultTumorBoardValues = {
  tb_name: '',
  tb_occurrence: {
    start_date: defaultStartDate,
    end_date: defaultStartDate,
    start_time: defaultStartTime,
    end_time: defaultEndTime,
    timezone: userTimeZone.value || Intl.DateTimeFormat().resolvedOptions().timeZone,
    location: '',
    frequency: 'DO NOT REPEAT',
    repeat: {},
  },
}

const initializeTumorBoard = async () => {
  // Function runs when a new tumor board is mounted. It will either init with
  // default values or use the values passed into this component via props.
  indexOfCurrentTb.value = props.indexOfTB
  timeZone.value = props.values?.tb_occurrence?.timezone || defaultTumorBoardValues.tb_occurrence.timezone

  nonModalFrequency.value = freqFromProps
    ? recurrenceOptions.find((element) => element.id === freqFromProps)
    : { id: 'DO NOT REPEAT', name: 'Do not Repeat' }
}

const removeTimeZoneFromString = (timeString) => {
  // Regular expression to match time zone patterns like +0000, -0500, +00:00, -05:00
  const timeZoneRegex = /[+-]\d{2}:?\d{2}$/
  return timeString.replace(timeZoneRegex, '')
}

const initializeTumorBoardObj = () => {
  const { tb_name = '', tb_occurrence = {} } = props.values

  // The following block of code will get the incoming date/time/timezone and
  // convert the time so users see the correct date/time relative to their set location.
  const incomingStartDate = formatDate(tb_occurrence.start_date || defaultTumorBoardValues.tb_occurrence.start_date)
  const incomingStartTime = removeTimeZoneFromString(tb_occurrence.start_time || defaultTumorBoardValues.tb_occurrence.start_time)
  const incomingEndDate = formatDate(tb_occurrence.end_date || defaultTumorBoardValues.tb_occurrence.start_date)
  const incomingEndTime = tb_occurrence.end_time || defaultTumorBoardValues.tb_occurrence.end_time
  const incomingTimezone = tb_occurrence.timezone || defaultTumorBoardValues.tb_occurrence.timezone

  // Convert start/end date/time to user's timezone.
  const dateTimeFormat = 'DD-MMM-YYYY HH:mm:ss'
  const localStartObj = moment.tz(`${incomingStartDate} ${incomingStartTime}`, dateTimeFormat, incomingTimezone).tz(userTimeZone.value)
  const localStartDate = localStartObj.format('YYYY-MM-DD')
  const localStartTime = localStartObj.format('HH:mm')

  const localEndObj = moment.tz(`${incomingEndDate} ${incomingEndTime}`, dateTimeFormat, incomingTimezone).tz(userTimeZone.value)
  const localEndDate = localEndObj.format('YYYY-MM-DD')
  const localEndTime = localEndObj.format('HH:mm')

  return {
    tb_name: tb_name || defaultTumorBoardValues.tb_name,
    tb_occurrence: {
      ...defaultTumorBoardValues.tb_occurrence,
      ...tb_occurrence,
      start_date: formatDate(localStartDate),
      end_date: formatDate(localEndDate),
      start_time: localStartTime,
      end_time: localEndTime,
      timezone: tb_occurrence.timezone || defaultTumorBoardValues.tb_occurrence.timezone,
      location: tb_occurrence.location,
    },
  }
}
const tumorBoardObj = ref(initializeTumorBoardObj())
// Saving for later comparison.
const originalTumorboardObj = ref(initializeTumorBoardObj())

const isFormEnabled = computed(() => {
  // Testing against the original tumorboard coming from props.
  const isEqual = _.isEqual(originalTumorboardObj.value, tumorBoardObj.value)
  const { tb_name } = tumorBoardObj.value
  const { start_date, start_time, end_time } = tumorBoardObj.value.tb_occurrence
  const isNameValid = tb_name?.trim().length > 0
  const isDateTimeValid = start_date && start_time && end_time
  const noErrors = !errorMessage.value && !locationErrorMessage.value
  const endTimeGreaterThanStartTime = start_time < end_time

  return isNameValid && isDateTimeValid && noErrors && endTimeGreaterThanStartTime && !isEqual
})

const loadTumorBoardData = async () => {
  try {
    if (props.indexOfTB >= 0 && props.values.length > props.indexOfTB) {
      const selectedTumorBoardData = props.values[props.indexOfTB]
      tumorBoardObj.value = selectedTumorBoardData
    }

  } catch (error) {
    console.error('Failed to load tumor board data:', error)
  }
}

const updateErrorMessage = (newMessage) => {
  errorMessage.value = newMessage
}

const updateLocationErrorMessage = (newMessage) => {
  locationErrorMessage.value = newMessage
}

const updateTumorBoardName = (newName) => {
  emit('form-change')
  tumorBoardObj.value = {
    ...tumorBoardObj.value,
    tb_name: newName,
  }
}

const updateTumorBoardLocation = (newLocation) => {
  emit('form-change')
  tumorBoardObj.value = {
    ...tumorBoardObj.value,
    tb_occurrence: {
      ...tumorBoardObj.value.tb_occurrence,
      location: newLocation,
    },
  }
}

const handleModalOpenClose = (field) => {
  if (field === 'recurrenceDropdown' && nonModalFrequency.value?.id !== 'DO NOT REPEAT') {
    isRecurrenceModalOpen.value = true
    isResetRecurrenceModal.value = false
  } else if (field === 'recurrenceDropdown') {
    recurrenceMessage.value = ''
  }
}

const handleModalClose = () => {
  isRecurrenceModalOpen.value = false
}

const handleModalCancel = () => {
  handleModalClose()
  nonModalFrequency.value = recurrenceOptions.find((element) => element.id === tumorBoardObj.value.tb_occurrence.frequency)
}

const handleTabClose = (event) => {
  emit('accordionTabClose', event)
}

const handleCloseForceUpdate = () => {
  forceUpdate.value = false
}

const handleDateTimeUpdate = (newDateTime) => {
  emit('form-change')
  const { startDate, startTime, endTime } = newDateTime
  const isFrequencyDoNotRepeat = nonModalFrequency.value.id === 'DO NOT REPEAT'
  tumorBoardObj.value = {
    ...tumorBoardObj.value,
    tb_occurrence: {
      ...tumorBoardObj.value.tb_occurrence,
      start_date: formatDate(startDate),
      // Eventually we will need to handle end date from the date component, but
      // for now the end_date will always be the start date unless there is recurrence
      end_date: isFrequencyDoNotRepeat ? formatDate(startDate) : tumorBoardObj.value.tb_occurrence.end_date,
      start_time: startTime,
      end_time: endTime,
      timezone: timeZone.value,
    },
  }
}

const setStateFromModal = (modifiedState) => {
  emit('form-change')
  nonModalFrequency.value = recurrenceOptions.find(element => element.id === modifiedState.frequency.id)
  recurrenceMessage.value = modifiedState.recurrenceMessage
  const isFrequencyDoNotRepeat = nonModalFrequency.value?.id === 'DO NOT REPEAT'

  // Set new repeat and index values
  let repeat_on
  let index = null
  switch (modifiedState?.frequency?.id) {
    case 'WEEKLY':
      repeat_on = modifiedState.repeatOn
      break
    case 'MONTHLY':
      if (modifiedState.monthlyRadioChange === 'onday') {
        repeat_on = modifiedState.monthOnDay
      } else {
        repeat_on = modifiedState.monthWeekRecurrence?.id
        index = modifiedState.monthOnThe?.id
      }
      break
    default:
      repeat_on = ''
  }

  // Update tumorBoardObj with new values
  tumorBoardObj.value = {
    ...tumorBoardObj.value,
    tb_occurrence: {
      ...tumorBoardObj.value.tb_occurrence,
      start_date: formatDate(modifiedState?.modalStartDate),
      end_date: formatDate(modifiedState?.modalEndDate),
      frequency: modifiedState.frequency?.id,
      repeat: {
        repeat_every: isFrequencyDoNotRepeat ? 1 : modifiedState.repeatEvery,
        ...(repeat_on && { repeat_on }),
        ...(index && { index }),
      },
    },
  }
}

const resetFields = () => {
  nonModalFrequency.value = { id: 'DO NOT REPEAT', name: 'Do not Repeat' }
  recurrenceMessage.value = ''
  tumorBoardObj.value = {
    ...defaultTumorBoardValues,
    tb_occurrence: {
      ...defaultTumorBoardValues.tb_occurrence,
      timezone: timeZone.value,
    },
  }

  isResetRecurrenceModal.value = true
}

const formatPayload = (payload, isUpdate) => {
  const { start_date, end_date, start_time, end_time, location } = payload.tb_occurrence
  const formattedPayload = {
    ...payload,
    tb_occurrence: {
      ...payload.tb_occurrence,
      start_date: formatDateToTBPayload(start_date),
      end_date: formatDateToTBPayload(end_date),
      start_time: start_time,
      end_time: end_time,
      timezone: userTimeZone.value,
      location: location,
    },
  }
  if (isUpdate) formattedPayload.tb_id = props.values.tb_id
  return formattedPayload
}

// Create/update tumorboard and send invite to tumorboard creator
const saveTumorBoard = async () => {
  const isUpdate = !!props.values?.tb_id
  const payload = formatPayload(tumorBoardObj.value, isUpdate)

  try {
    const response = await putTumorBoard(payload)
    handleTumorBoardResponse(response, isUpdate)
    await handleCalendarInvite(response)
  } catch (error) {
    handleError(error, isUpdate)
  }
}

const handleTumorBoardResponse = (response, isUpdate) => {
  if (response?.detail) {
    handleFailedCall(response.detail)
  } else {
    const message = isUpdate ? 'updated' : 'added'
    showToast('success', `Tumor Board has been ${message} successfully`)
    emit('reset-confirmation')
    emit('reloadComponent', response)
    if (!isUpdate) { resetFields() }
  }
}

const handleFailedCall = (detail) => {
  if (detail.includes('Not Able to create Calendar Dates') || detail.includes('past dates')) {
    showToast('error', 'There are no upcoming sessions for this tumor board')
  }
}

const handleError = (error, isUpdate) => {
  const errorMessage = isUpdate
    ? error?.detail || 'Failed to update Tumor Board'
    : error?.detail || 'Failed to add Tumor Board'
  showToast('error', errorMessage)
}

const handleCalendarInvite = async (response) => {
  if (!response?.details && response?.tb_id) {

    const f = editModeActive.value ? updateCalendarInvite : sendCalendarInvite
    const inviteResponse = await f(response.tb_id)
    const message = editModeActive.value ? 'Changes saved' : 'Invite sent successfully'

    const status = inviteResponse.message === message ? 'success' : 'error'
    showToast(status, inviteResponse.message ? inviteResponse.message : 'There was a problem adding the tumorboard meeting to your calendar.')
  }
}

watch(() => props.values, (newTumorBoards) => {
  if (!newTumorBoards) { return }

  editModeActive.value = Object.keys(props.values).length > 0
  accordionHeader.value = editModeActive.value ? 'Edit Tumor Board' : 'Add Tumor Board'

}, { immediate: true })

// Need to update tumorBoardObj when the frequency changes to 'DO NOT REPEAT'
watch(nonModalFrequency, () => {
  if (nonModalFrequency.value.id !== 'DO NOT REPEAT') { return }

  tumorBoardObj.value = {
    ...tumorBoardObj.value,
    tb_occurrence: {
      ...tumorBoardObj.value.tb_occurrence,
      frequency: defaultTumorBoardValues.tb_occurrence.frequency,
      repeat: {
        repeat_every: 1,
      },
    },
  }
})

watch(() => props.values, () => {
  tumorBoardObj.value = initializeTumorBoardObj()
  originalTumorboardObj.value = initializeTumorBoardObj()
}, { deep: true })

onMounted(async () => {
  await loadTumorBoardData()
  await initializeTumorBoard()
})
</script>

<style lang="scss" scoped>
.label-span {
  color: var(--Grey600);
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
}

.field-margin {
  margin-top: 2%;
  margin-bottom: 1%;
}

.input-text {
  background-color: var(--White);
}

.primary-filled-button {
  margin: 2rem .1rem;
}

.error-message-div p {
  color: var(--ErrorRedDark);
}

.meeting-div {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.recurrence-message {
  background-color: var(--Grey300);
  padding: 7px 10px;
  border-radius: 5px;
  font-size: 0.9rem;
}

.how-long-mtg .ml-2 {
  margin: 0 !important;
  padding-right: 5px;
  display: flex;
  align-items: center;
}

.how-long-mtg {
  background-color: var(--Grey300);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 7px;
  flex-wrap: nowrap;
  margin-left: 1rem;
  font-size: 0.8rem;
}
</style>
